import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { ConfirmationService } from 'primeng/api';

import { APP_CONST } from '../../constants/constants';
import { QUESTION_PAGE_UI } from 'src/app/constants/ui-strings';
import { StateManagementService } from 'src/app/services/state-management.service';
import { TestService } from 'src/app/services/test.service';

@Component({
  selector: 'app-assessment-page',
  templateUrl: './assessment-page.component.html',
  styleUrl: './assessment-page.component.scss',
})
export class AssessmentPageComponent implements OnInit, OnDestroy {
  fullNavbar: boolean = false;
  showTestQuestions: boolean = false;
  tabSwitchCount: number = 0;
  maxTabSwitches: number;
  isTabSwitch: boolean = false;
  visibilityTimeout: ReturnType<typeof setTimeout>;
  isFullScreenEnforced: boolean;
  isCameraEnforced: boolean;
  private visibilityChangeHandler: () => void;
  private ignoreTabSwitchEvents: boolean = false;
  private releaseIgnoreTimeout: ReturnType<typeof setTimeout> | undefined;
  private fullscreenExitDialogOpen: boolean = false;
  private blurTimeout: ReturnType<typeof setTimeout> | undefined;
  // Delay before a `blur` event is counted as a real tab switch. Native browser
  // prompts (e.g. Firefox "Save password?", autofill, translate) briefly steal
  // focus and return it; deferring the count avoids those false positives.
  private static readonly BLUR_CONFIRM_DELAY_MS = 1500;

  constructor (
    private router: Router,
    private confirmationService: ConfirmationService,
    private stateManagementService: StateManagementService,
    private testService: TestService
  ) {}

  ngOnInit(): void {
    this.isFullScreenEnforced = this.testService.testData().proctor_settings.enforce_fullscreen;
    this.maxTabSwitches = parseInt(this.testService.testData().proctor_settings.max_tab_allowed);
    this.isCameraEnforced = this.testService.testData().proctor_settings.enforce_camera;
    
    const currentUrl: string = this.router.url;
    if (currentUrl.endsWith(APP_CONST.test)) {
      this.showTestQuestions = true;
      this.fullNavbar = true;
    }
    this.visibilityChangeHandler = this.handleTabSwitch.bind(this);
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    window.addEventListener('blur', this.handleBlur);
    window.addEventListener('focus', this.handleWindowFocus);

    // Camera permission prompts can trigger blur/visibility events even if the user didn't actually leave the test.
    // If camera is enforced and permission is still "prompt", temporarily ignore tab-switch counting until resolved.
    if (this.isCameraEnforced) {
      void this.ignoreTabSwitchDuringCameraPrompt();
    }
  }

  private async ignoreTabSwitchDuringCameraPrompt(): Promise<void> {
    const navAny = navigator as any;
    const permissions = navAny?.permissions;
    if (!permissions?.query) return;

    try {
      // PermissionName may not include "camera" depending on TS/lib.dom version; cast keeps this build-safe.
      const status: PermissionStatus = await permissions.query({ name: 'camera' as any });
      if (status.state !== 'prompt') return;

      this.ignoreTabSwitchEvents = true;

      // Release ignore once the user returns focus after closing the prompt.
      window.addEventListener(
        'focus',
        () => {
          this.ignoreTabSwitchEvents = false;
        },
        { once: true }
      );

      // Or as soon as permission resolves (granted/denied).
      status.addEventListener?.('change', () => {
        if (status.state !== 'prompt') this.ignoreTabSwitchEvents = false;
      });

      // Hard fallback: don't ignore forever if the browser doesn't fire focus/change as expected.
      this.releaseIgnoreTimeout = setTimeout(() => {
        this.ignoreTabSwitchEvents = false;
      }, 15000);
    } catch {
      // If Permissions API isn't available/allowed for camera, do nothing (default behavior remains).
    }
  }

  handleTabSwitch(): void {
    if (this.ignoreTabSwitchEvents) return;
    if (document.hidden) {
      this.visibilityTimeout = setTimeout(() => {
        if (document.hidden) {
          this.tabSwitchCount++;
          this.isTabSwitch = true;
          if (this.tabSwitchCount >= this.maxTabSwitches) {
            this.submitTest();
          }
        }
      }, 3000);
    } else {
      clearTimeout(this.visibilityTimeout);
    }
  }

  handleBlur = (): void => {
    if (this.ignoreTabSwitchEvents) return;
    if (this.blurTimeout) return;
    // Defer counting; if focus returns quickly (browser doorhanger / autofill)
    // `handleWindowFocus` cancels this. Only count if the page is genuinely
    // hidden or unfocused after the confirm delay.
    this.blurTimeout = setTimeout(() => {
      this.blurTimeout = undefined;
      if (this.ignoreTabSwitchEvents) return;
      if (!document.hidden && document.hasFocus()) return;
      this.tabSwitchCount++;
      this.isTabSwitch = true;
      if (this.tabSwitchCount >= this.maxTabSwitches) {
        this.isTabSwitch = false;
        this.submitTest();
      }
    }, AssessmentPageComponent.BLUR_CONFIRM_DELAY_MS);
  };

  private handleWindowFocus = (): void => {
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout);
      this.blurTimeout = undefined;
    }
  };

  submitTest(): void {
    this.testService.submitTest();
  }

  /**
   * Handles fullscreen exit for all browsers.
   * Uses a guard to avoid duplicate dialogs when multiple fullscreen events fire (e.g. Safari).
   */
  private handleFullscreenChange(): void {
    if (!this.isFullScreenEnforced) return;
    const isFullscreen = this.testService.isFullscreen();
    if (isFullscreen) return;
    if (this.fullscreenExitDialogOpen) return;

    this.fullscreenExitDialogOpen = true;
    this.confirmationService.confirm({
      message: QUESTION_PAGE_UI.exit_full_screen_message,
      header: QUESTION_PAGE_UI.exit_full_screen_header,
      icon: 'pi pi-window-maximize',
      accept: () => {
        this.fullscreenExitDialogOpen = false;
        this.submitTest();
      },
      reject: () => {
        this.fullscreenExitDialogOpen = false;
        this.testService.goFullscreen(this.isFullScreenEnforced).catch(() => {
          // Error already handled in service
        });
      },
      closeOnEscape: false
    });
  }

  // Standard fullscreen change event (Chrome, Edge, Opera)
  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    this.handleFullscreenChange();
  }

  // Firefox fullscreen change event
  @HostListener('document:mozfullscreenchange')
  onMozFullscreenChange(): void {
    this.handleFullscreenChange();
  }

  // WebKit/Safari fullscreen change event
  @HostListener('document:webkitfullscreenchange')
  onWebkitFullscreenChange(): void {
    this.handleFullscreenChange();
  }

  // IE/Edge (old) fullscreen change event
  @HostListener('document:MSFullscreenChange')
  onMSFullscreenChange(): void {
    this.handleFullscreenChange();
  }

  @HostListener('document:contextmenu', ['$event'])
  onRightClick(event: MouseEvent): void {
    event.preventDefault();
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (
      event.key === 'F12' ||
      (event.ctrlKey && event.shiftKey && (event.key === 'I' || event.key === 'J'))
    ) {
      event.preventDefault();
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  wornBrowserReloadAndClose($event: BeforeUnloadEvent): void {
    $event.preventDefault();
    $event.returnValue = '';
  }

  ngOnDestroy(): void {
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }
    window.removeEventListener('blur', this.handleBlur);
    window.removeEventListener('focus', this.handleWindowFocus);
    if (this.visibilityTimeout) {
      clearTimeout(this.visibilityTimeout);
    }
    if (this.releaseIgnoreTimeout) {
      clearTimeout(this.releaseIgnoreTimeout);
    }
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout);
    }
    this.confirmationService.close();
    this.stateManagementService.isTestRouteAllowed = false;
  }
}
